import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Table } from 'antd';
import cx from 'classnames';
import { ColumnsType } from 'antd/lib/table/interface';

import {
  AverageRatesByCustomerReportItemViewModel,
  AverageRatesSortFieldEnum,
  SortingEnum,
} from 'api/backofficeApiClient';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { getAverageRatesReportData } from 'redux/reducers/homeDashboardSlice';
import {
  getDateRangeTillLastSunday,
  getDateWithoutTime,
} from 'utils/dateUtils';

import styles from '../../UserHomePage.module.less';
import commonStyles from './AverageRatesCustomerReportPreview.module.less';

const columns: ColumnsType<AverageRatesByCustomerReportItemViewModel> = [
  {
    title: 'Customer',
    key: AverageRatesSortFieldEnum.Customer,
    render: (record: AverageRatesByCustomerReportItemViewModel) =>
      record.customerName,
  },
  {
    title: 'Avg Bill Rate',
    key: AverageRatesSortFieldEnum.AvgBillRate,
    render: (record: AverageRatesByCustomerReportItemViewModel) =>
      record.avgBillRate,
  },
  {
    title: 'Avg Pay Rate',
    key: AverageRatesSortFieldEnum.AvgPayRate,
    render: (record: AverageRatesByCustomerReportItemViewModel) =>
      record.avgPayRate,
  },
  {
    title: 'Spread $',
    key: AverageRatesSortFieldEnum.Spread,
    render: (record: AverageRatesByCustomerReportItemViewModel) =>
      record.spread,
  },
  {
    title: 'Markup %',
    key: AverageRatesSortFieldEnum.Markup,
    render: (record: AverageRatesByCustomerReportItemViewModel) =>
      record.markup,
  },
];

const dateRangeDurationInWeeks = 7;

const date = getDateRangeTillLastSunday(dateRangeDurationInWeeks);

export default function AverageRatesCustomerReportPreview() {
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [
    averageRatesCustomerReportPreviewData,
    setAverageRatesCustomerReportPreviewData,
  ] = useState<AverageRatesByCustomerReportItemViewModel[]>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const loadAverageRatesCustomerReportPreviewData = useCallback(async () => {
    const result = await dispatch(
      getAverageRatesReportData({
        pageSize: 5,
        sortField: AverageRatesSortFieldEnum.Markup,
        sortType: SortingEnum.Desc,
        startDate: getDateWithoutTime(date?.startDate),
        endDate: getDateWithoutTime(date?.endDate),
      }),
    );

    if (getAverageRatesReportData.fulfilled.match(result)) {
      setAverageRatesCustomerReportPreviewData(result.payload.items);
    } else {
      setAverageRatesCustomerReportPreviewData(undefined);
    }
  }, [dispatch]);

  const loadInitialData = useCallback(async () => {
    await loadAverageRatesCustomerReportPreviewData();

    setIsInitialDataLoaded(true);
  }, [loadAverageRatesCustomerReportPreviewData]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  return (
    <div className={styles.tableWrapper}>
      <div className={styles.title}>
        <Link to="/average-rates-customer-report-details">
          Average Bill and Pay Rates by Customer
        </Link>
        <Button
          type="primary"
          onClick={() => navigate('/average-rates-customer-report-details')}
        >
          View Details
        </Button>
      </div>

      <Table
        className={cx(commonStyles.table, styles.table, 'custom-table')}
        columns={columns}
        pagination={false}
        loading={!isInitialDataLoaded}
        dataSource={averageRatesCustomerReportPreviewData}
        rowKey={(record) => record?.customerKey as string}
        locale={{
          emptyText: 'You currently do not have any data in the report.',
        }}
      />
    </div>
  );
}
